/**
 * POS Gate — wraps /pos and /pos/cart. If no shift is active, shows
 * POSShiftStart. If a shift is active, renders the requested step: the
 * product picker (step one) or the cart review (step two).
 *
 * The shift is kept in localStorage, so it survives a refresh — and also an
 * event being deleted or archived meanwhile. A shift on an event that no
 * longer exists used to leave the POS open with every product at stock 0 and
 * no way to charge, which reads as "the app won't let me input". The gate now
 * ends such a shift and tells the cashier why.
 *
 * All branches share the same full-screen layout (no AppShell).
 */

import { useEffect } from 'react';
import { usePosShiftStore } from '@/store/posShift';
import { useEvent } from '@/hooks/useEvents';
import { useCart } from '@/store/cart';
import { usePosCheckout } from '@/store/posCheckout';
import POSShiftStart from '@/components/pos/POSShiftStart';
import POS from '@/routes/POS';
import POSCartReview from '@/routes/POSCartReview';

export default function POSGate({ view = 'products' }: { view?: 'products' | 'cart' }) {
  const eventId = usePosShiftStore((s) => s.eventId);
  const eventName = usePosShiftStore((s) => s.eventName);
  const endShift = usePosShiftStore((s) => s.endShift);
  const { data: event, isSuccess } = useEvent(eventId);

  const gone = !!eventId && isSuccess && (!event || event.status === 'archived' || event.status === 'void');

  useEffect(() => {
    if (!gone) return;
    useCart.getState().clear();
    usePosCheckout.getState().resetSale();
    endShift(
      `Shift sebelumnya diakhiri otomatis: event "${eventName ?? ''}" sudah tidak ada atau tidak aktif lagi. Pilih event lain untuk mulai.`,
    );
  }, [gone, eventName, endShift]);

  if (!eventId || gone) {
    return <POSShiftStart />;
  }
  return view === 'cart' ? <POSCartReview /> : <POS />;
}
